import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, IconButton, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import PatientsIcon from 'icons/PatientsIcon';
import { toTitleCase } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { IPhotoSize } from 'types/types';
import useGlobalStyles from 'styles/globalStyles';
import PersonIcon from '@mui/icons-material/Person';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import UserActionButtons from 'components/tables/ActionButtons/UserActionButtons';
import { tableColumnProps } from 'common/constants';
import ChangeUserRole from '../ActionInputs/ChangeUserRole';
import { OrdersFiltersDto } from 'types/dto/OrdersFiltersDto';
import { ListOrdersDto } from 'types/dto/ListOrdersDto';
import OrdersFilters from './OrdersFilters';
import { useGetOrders } from 'hooks/useOrders';
import OrderStatus from 'components/statuses/OrderStatus';


export default function OrdersTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState<OrdersFiltersDto>({
    status: '',
  });

  const requestParams: ListOrdersDto = {
    page: page + 1,
    size,
    ...filters,
  };


  const { data, refetch, isLoading } = useGetOrders(requestParams, {
    onSuccess: () => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'trackingId',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text='Tracking ID' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`#${params.row.trackingId}`} />
          </Box>
        )
      }
    },
    {
      field: 'customer',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Customer' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.orderCustomer.fullName} />
          </Box>
        )
      }
    },
    {
      field: 'agent',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Agent' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.agent.fullName} />
          </Box>
        )
      }
    },
    {
      field: 'storeOrders',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Store Orders' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.storeOrders?.length} />
          </Box>
        )
      }
    },
    {
      field: 'status',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Status' />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            {<OrderStatus status={params.row.status} />}
          </Box>
        )
      },
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <PatientsIcon color={colors.primary} />
            </Box>
            <CustomText text="All Orders" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <OrdersFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
            refetch();
          }}
          onReset={() => {
            setFilters({ status: '' });
            setPage(0);
            refetch();
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onRowClick={(params) => setSelectedId(params.row.id)}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
